import { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, LinearProgress, Avatar } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import adminAxios from '../../utils/adminAxios';
import Sparkline from '../../components/admin/charts/Sparkline';
import LineActivityChart from '../../components/admin/charts/LineActivityChart';
import DonutChart from '../../components/admin/charts/DonutChart';

const PALETA = ['#7495F8', '#0E2465', '#9DE1D5', '#59C39A', '#80A3FF'];

// Tendencia simple a partir de la serie (último mes vs anterior).
const trendSerie = (serie = []) => {
  if (serie.length < 2) return null;
  const a = serie[serie.length - 2];
  const b = serie[serie.length - 1];
  if (a === 0) return b > 0 ? 100 : null;
  return Math.round(((b - a) / a) * 1000) / 10;
};

const tiempoRelativo = (fecha) => {
  const diff = Date.now() - new Date(fecha).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Hace un momento';
  if (min < 60) return `Hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Hace ${h} h`;
  const d = Math.floor(h / 24);
  return `Hace ${d} d`;
};

const colorActividad = { usuario: '#7495F8', servicio: '#59C39A', reserva: '#FFD074' };

const Tarjeta = ({ icon, valor, label, serie, color, trend }) => {
  const t = trend ?? trendSerie(serie);
  const positivo = t === null ? null : t >= 0;
  return (
    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Avatar variant="rounded" sx={{ bgcolor: '#EEF2FF', color: 'secondary.main', width: 44, height: 44 }}>
          {icon}
        </Avatar>
        {positivo !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: positivo ? 'success.main' : 'error.main' }}>
            {positivo ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {positivo ? '+' : ''}{t}%
            </Typography>
          </Box>
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>{valor}</Typography>
      <Typography variant="body2" sx={{ color: '#737791', mb: 1 }}>{label}</Typography>
      <Box sx={{ height: 44 }}><Sparkline data={serie} color={color} /></Box>
    </Paper>
  );
};

const Panel = ({ titulo, sub, children, sx }) => (
  <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', height: '100%', ...sx }}>
    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{titulo}</Typography>
    {sub && <Typography variant="body2" sx={{ color: '#737791', mb: 2 }}>{sub}</Typography>}
    {children}
  </Paper>
);

const FilaSalud = ({ label, pct, valorTexto, color = '#7495F8' }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
      <Typography variant="body2" sx={{ color: '#475569' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>{valorTexto}</Typography>
    </Box>
    <LinearProgress variant="determinate" value={Math.min(pct, 100)}
      sx={{ height: 8, borderRadius: 4, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 } }} />
  </Box>
);

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    adminAxios.get('/api/admin/dashboard')
      .then(res => setData(res.data))
      .catch(err => console.error('Error dashboard', err.message))
      .finally(() => setCargando(false));
  }, []);

  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }
  if (!data) return null;

  const { tarjetas, sparklines, actividadMensual, distribucionCategorias, salud, actividadReciente } = data;

  const cards = [
    { icon: <PeopleAltOutlinedIcon />, valor: tarjetas.usuarios.toLocaleString('es-MX'), label: 'Usuarios Totales', serie: sparklines.usuarios, color: '#7495F8', trend: tarjetas.trendUsuarios },
    { icon: <StorefrontOutlinedIcon />, valor: tarjetas.proveedoresActivos.toLocaleString('es-MX'), label: 'Proveedores Activos', serie: sparklines.servicios, color: '#59C39A' },
    { icon: <HandymanOutlinedIcon />, valor: tarjetas.servicios.toLocaleString('es-MX'), label: 'Servicios Publicados', serie: sparklines.servicios, color: '#80A3FF' },
    { icon: <VerifiedUserOutlinedIcon />, valor: tarjetas.verificacionesPendientes.toLocaleString('es-MX'), label: 'Verificaciones Pendientes', serie: sparklines.usuarios, color: '#FFD074' },
  ];

  return (
    <Box>
      {/* Tarjetas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {cards.map((c, i) => (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={i}><Tarjeta {...c} /></Grid>
        ))}
      </Grid>

      {/* Actividad mensual + Distribución */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Panel titulo="Actividad de la plataforma" sub={`Servicios vs reservas — ${new Date().getFullYear()}`}>
            <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#7495F8' }} />
                <Typography variant="caption" sx={{ color: '#737791' }}>Servicios</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#59C39A' }} />
                <Typography variant="caption" sx={{ color: '#737791' }}>Reservas</Typography>
              </Box>
            </Box>
            <LineActivityChart
              data={actividadMensual}
              series={[
                { key: 'servicios', label: 'Servicios', color: '#7495F8' },
                { key: 'reservas', label: 'Reservas', color: '#59C39A' },
              ]}
            />
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Panel titulo="Distribución por categoría" sub="Servicios activos">
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <DonutChart segments={distribucionCategorias} colors={PALETA} />
            </Box>
            {distribucionCategorias.map((seg, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PALETA[i % PALETA.length], flexShrink: 0 }} />
                  <Typography variant="body2" noWrap sx={{ color: '#475569' }}>{seg.name}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>{seg.pct}%</Typography>
              </Box>
            ))}
          </Panel>
        </Grid>
      </Grid>

      {/* Salud + Actividad reciente */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Panel titulo="Salud de la Plataforma" sub="Métricas reales del sistema">
            <FilaSalud label="Servicios activos" pct={salud.serviciosActivos.pct}
              valorTexto={`${salud.serviciosActivos.valor}/${salud.serviciosActivos.total} (${salud.serviciosActivos.pct}%)`} color="#7495F8" />
            <FilaSalud label="Páginas activas" pct={salud.paginasActivas.pct}
              valorTexto={`${salud.paginasActivas.valor}/${salud.paginasActivas.total} (${salud.paginasActivas.pct}%)`} color="#80A3FF" />
            <FilaSalud label="Reservas completadas" pct={salud.reservasCompletadas.pct}
              valorTexto={`${salud.reservasCompletadas.valor}/${salud.reservasCompletadas.total} (${salud.reservasCompletadas.pct}%)`} color="#59C39A" />
            <FilaSalud label="Verificaciones pendientes" pct={salud.verificacionesPendientes > 0 ? 100 : 4}
              valorTexto={`${salud.verificacionesPendientes} pendientes`} color="#FFD074" />
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Panel titulo="Actividad Reciente" sub="Últimas acciones en la plataforma">
            {actividadReciente.length === 0 && (
              <Typography variant="body2" sx={{ color: '#737791' }}>Sin actividad reciente.</Typography>
            )}
            {actividadReciente.map((a, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.2, borderBottom: i < actividadReciente.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', mt: 0.6, flexShrink: 0, bgcolor: colorActividad[a.tipo] || '#94A3B8' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{a.titulo}</Typography>
                  <Typography variant="caption" sx={{ color: '#737791' }}>
                    {a.sub} · {tiempoRelativo(a.fecha)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
